import { SubscriptionQuery } from '@awork/_shared/state/subscription.query'
import type { OnDestroy } from '@angular/core'
import { DestroyRef, effect, Injectable, NgZone, signal, untracked, inject } from '@angular/core'
import type { Subscription } from 'rxjs'
import { ReplaySubject, from } from 'rxjs'
import type { ConnectionStatus } from '@capacitor/network'
import { Network } from '@capacitor/network'
import { AppQuery } from '@awork/core/state/app.query'
import { LogService } from '@awork/_shared/services/log-service/log.service'
import { environment } from '../environments/environment'
import { BrowserService } from './shared/services/browser-service/browser.service'
import { PlanStatus } from '@awork/_shared/models/subscription.types'
import isTest from '@awork/_shared/functions/is-test'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'
import { AutoUnsubscribe } from '@awork/_shared/decorators/auto-unsubscribe'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'

export enum AppEvent {
  Started,
  Paused,
  Resumed,
  LostConnection,
  ConnectionReturned,
  LoggedIn,
  WorkspaceSwitch
}

/** Appearance saved on this device. System follows the native or browser theme. */
export type MobileThemeMode = 'light' | 'dark' | 'system'

const THEME_STORAGE_KEY = 'awMobileTheme'

@AutoUnsubscribe()
@Injectable({
  providedIn: 'root'
})
export class AppService implements OnDestroy {
  private appQuery = inject(AppQuery)
  private logService = inject(LogService)
  private browserService = inject(BrowserService)
  private subscriptionQuery = inject(SubscriptionQuery)
  private ngZone = inject(NgZone)
  private readonly destroyRef = inject(DestroyRef)

  events$ = new ReplaySubject<{ type: AppEvent; timestamp: Date }>(10)

  private readonly _onlineStatus = signal(navigator.onLine)
  online$ = toObservable(this._onlineStatus)

  isInWifi = true
  subscriptions: Subscription[] = []

  private readonly ios = signal(false)
  private readonly android = signal(false)
  readonly isIos = this.ios.asReadonly()
  readonly isAndroid = this.android.asReadonly()
  readonly darkMode = signal(false)
  private readonly selectedThemeMode = signal<MobileThemeMode>(this.readThemeMode())
  readonly themeMode = this.selectedThemeMode.asReadonly()
  private systemDarkMode = false
  /** Compatibility property for callers that use the boolean theme API. */
  get isDarkMode(): boolean {
    return this.darkMode()
  }
  /** Sets the shared theme state. @param value Whether dark mode is active. */
  set isDarkMode(value: boolean) {
    this.darkMode.set(value)
  }
  /**
   * Counts native theme pushes. iOS re-applies the configured status-bar background whenever the
   * Capacitor view reappears, so consumers must re-sync on every push, not only on a value change.
   */
  readonly nativeThemeSync = signal(0)
  private hasNativeTheme = false

  get onlineStatus() {
    return this._onlineStatus.asReadonly()
  }

  get isOnline() {
    return this._onlineStatus()
  }

  constructor() {
    this.emitAppEvents()
    this.getPlatform()
    this.onFirstRun()
    this.getAppState()
    this.getNetworkStatus()
    this.detectTheme()
  }

  ngOnDestroy(): void {}

  /**
   * Emits app events (app started, logged in, workspace switched)
   */
  private emitAppEvents(): void {
    this.logService.logDebug({
      name: 'start: ',
      message: 'app start'
    })

    this.events$.next({ type: AppEvent.Started, timestamp: new Date() })

    // Query isLoggedIn and isSwitchingWorkspace flags to emit respective events
    effect(() => {
      const isLoggedIn = this.appQuery.query('isLoggedIn')

      if (!isLoggedIn()) {
        return
      }

      untracked(() => {
        console.log('App Login (detected by store)')
        this.events$.next({ type: AppEvent.LoggedIn, timestamp: new Date() })
      })
    })

    effect(() => {
      const isSwitchingWorkspace = this.appQuery.query('isSwitchingWorkspace')

      if (!isSwitchingWorkspace()) {
        return
      }

      untracked(() => {
        console.log('Switching workspace')
        this.events$.next({ type: AppEvent.WorkspaceSwitch, timestamp: new Date() })
      })
    })
  }

  /**
   * Gets the device platform name
   */
  private getPlatform(): void {
    from(Device.getInfo?.() ?? Promise.resolve({ platform: 'web' }))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(deviceInfo => {
        this.ios.set(deviceInfo.platform === 'ios')
        this.android.set(deviceInfo.platform === 'android')
      })
  }

  /**
   * Detects if the app is in the background or foreground
   */
  private getAppState(): void {
    this.subscriptions.push(
      from(App.getState?.() ?? Promise.resolve({ isActive: true })).subscribe(state => {
        // App goes to foreground
        if (state.isActive) {
          this.logService.logDebug({
            name: 'resume: ',
            message: 'app resume'
          })

          this.events$.next({ type: AppEvent.Resumed, timestamp: new Date() })
        }
        // App goes to background
        else {
          console.log('App paused')
          this.events$.next({ type: AppEvent.Paused, timestamp: new Date() })
        }
      })
    )
  }

  /**
   * Gets the network status to show/hide Offline mode
   */
  private getNetworkStatus(): void {
    let wifiTimeout: ReturnType<typeof setTimeout> | undefined
    const listener =
      Network.addListener?.('networkStatusChange', (status: ConnectionStatus) => {
        if (this.destroyRef.destroyed) {
          return
        }

        clearTimeout(wifiTimeout)
        this.ngZone.run(() => {
          // App gets online
          if (status.connected) {
            console.log('App connection returned')
            this.events$.next({ type: AppEvent.ConnectionReturned, timestamp: new Date() })
            this._onlineStatus.set(true)

            wifiTimeout = setTimeout(() => {
              if (status.connectionType === 'wifi') {
                console.log('wifi true')
                this.isInWifi = true
              }
            }, 2000)
          }

          // App gets offline
          else {
            console.log('App connection lost')
            this.events$.next({ type: AppEvent.LostConnection, timestamp: new Date() })
            this._onlineStatus.set(false)
            this.isInWifi = false
            console.log('wifi false')
          }
        })
      }) ?? Promise.resolve({ remove: () => {} })

    this.destroyRef.onDestroy(() => {
      clearTimeout(wifiTimeout)
      void listener.then(handle => handle.remove())
    })

    if (environment === 'local') {
      this.subscriptions.push(
        this.browserService.online$.subscribe(isOnline => {
          this._onlineStatus.set(isOnline)
          this.isInWifi = isOnline
        })
      )
    }
  }

  /**
   * Detects dark/light theme
   */
  private detectTheme(): void {
    // iOS 26's WKWebView does not reliably report prefers-color-scheme to
    // embedded pages, so the native shell pushes the trait collection through
    // this hook (see ThemeViewController). It wins over the media query.
    const themeWindow = window as { __setNativeTheme?: (isDark: boolean) => void }
    const setNativeTheme = (isDark: boolean) => {
      this.hasNativeTheme = true
      this.systemDarkMode = isDark
      this.applyTheme(isDark)
      this.nativeThemeSync.update(count => count + 1)
    }

    themeWindow.__setNativeTheme = setNativeTheme

    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)')

    this.systemDarkMode = prefersDark.matches
    this.applyTheme(prefersDark.matches)

    // The initial read can race the webview's trait setup on cold start, and the
    // user can switch the system appearance while the app runs.
    const onThemeChange = (event: MediaQueryListEvent) => {
      if (!this.hasNativeTheme) {
        this.systemDarkMode = event.matches
        this.applyTheme(event.matches)
      }
    }

    prefersDark.addEventListener('change', onThemeChange)
    this.destroyRef.onDestroy(() => {
      prefersDark.removeEventListener('change', onThemeChange)

      if (themeWindow.__setNativeTheme === setNativeTheme) {
        delete themeWindow.__setNativeTheme
      }
    })
  }

  /**
   * Applies the detected color scheme.
   * @param {boolean} isDark - Whether the system prefers dark mode.
   */
  private applyTheme(isDark: boolean): void {
    const mode = this.themeMode()
    this.darkMode.set(mode === 'dark' || (mode === 'system' && isDark))
  }

  /**
   * Saves the appearance choice and applies it at once.
   * @param {MobileThemeMode} mode Appearance to use on this device.
   */
  setThemeMode(mode: MobileThemeMode): void {
    this.selectedThemeMode.set(mode)
    this.applyTheme(this.systemDarkMode)

    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode)
    } catch {
      // Keep the choice active when device storage is unavailable.
    }
  }

  /**
   * Reads a valid saved appearance, or uses the system theme.
   * @returns {MobileThemeMode} The saved theme mode.
   */
  private readThemeMode(): MobileThemeMode {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY)

      if (stored === 'light' || stored === 'dark') {
        return stored
      }
    } catch {
      // Device storage can be unavailable at startup.
    }

    return 'system'
  }

  /**
   * Checks if the user can invite users on iOS
   * @returns {boolean}
   */
  isAllowedToShowPaywallDueToUsers(): boolean {
    if (this.isIos()) {
      const subscription = this.subscriptionQuery.getSubscription()

      // Check subscription limitations
      if ((!subscription || subscription.remainingUsers <= 0) && subscription.status !== PlanStatus.InTrial) {
        return false
      }
    }

    return true
  }

  /**
   * Checks if the user can create projects on iOS
   * @returns {boolean}
   */
  isAllowedToShowPaywallDueToProjects(): boolean {
    if (this.isIos()) {
      const subscription = this.subscriptionQuery.getSubscription()

      return !!subscription
    }

    return true
  }

  /**
   * Reloads the App for the first time after installation
   * Only for iPad (hotfix for changeDetection issues)
   * @private
   */
  private onFirstRun(): void {
    if (!this.isIos() || isTest()) {
      return
    }

    const appInit = localStorage.getItem('appInit')

    if (!appInit) {
      localStorage.setItem('appInit', 'true')

      location.reload()
    }
  }
}
